import {useState} from 'react';
import {Button} from "@/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import {Input} from "@/components/ui/input"
import {Label} from "@/components/ui/label"
import {useTranslation} from "react-i18next";
import {Pencil, Trash2, Plus} from "lucide-react";

interface ColumnMap {
    [key: string]: string;
}

interface TableFloatUIProps {
    tableItems: any[],
    tableTitle: string,
    columns: ColumnMap,
    saveHandler: (item: any, itemId: number) => void;
    deleteHandler: (item: any) => void;
}

const TableFloatUI = ({tableItems, tableTitle, columns, saveHandler, deleteHandler}: TableFloatUIProps) => {
    const {t} = useTranslation();
    const columnProperties = Object.keys(columns);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const handleEdit = (item: any) => {
        setSelectedItem(item);
        setIsDialogOpen(true);
    };

    const handleSave = (item: any, id: number) => {
        saveHandler(item, id);
        handleCloseDialog();
    };

    const handleCloseDialog = () => {
        setSelectedItem(null);
        setIsDialogOpen(false);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-lg font-semibold text-text-1">{tableTitle}</h2>
                </div>
                <Button
                    onClick={() => setIsDialogOpen(true)}
                    size="lg"
                    className="font-semibold"
                >
                    <Plus className="mr-2 h-4 w-4" />
                    {t("common.add")} {tableTitle}
                </Button>
            </div>
            
            <div className="rounded-xl border border-border bg-bg-card overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-bg-elevated border-b-2 border-border">
                            <tr>
                                {columnProperties.map((property, idx) => (
                                    <th key={idx} className="px-6 py-3 text-left text-sm font-semibold text-text-2">
                                        {columns[property]}
                                    </th>
                                ))}
                                <th className="px-6 py-3 text-right text-sm font-semibold text-text-2">
                                    {t("common.actions")}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-bg-card divide-y divide-border">
                            {tableItems.length === 0 ? (
                                <tr>
                                    <td colSpan={columnProperties.length + 1} className="px-6 py-12 text-center text-sm text-text-3">
                                        {t("common.noItems", "No items found")}
                                    </td>
                                </tr>
                            ) : (
                                tableItems.map((item, idx) => (
                                    <tr key={idx} className="bg-bg-card hover:bg-bg-elevated/50 transition-colors">
                                        {columnProperties.map((property, index) => (
                                            <td key={index} className="px-6 py-4 text-sm text-text-1 whitespace-normal break-words">
                                                {item[property] || <span className="text-text-3">—</span>}
                                            </td>
                                        ))}
                                        <td className="px-6 py-4 text-right whitespace-nowrap">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleEdit(item)}
                                                    className="h-9 w-9 text-text-2 hover:text-text-1 hover:bg-bg-elevated"
                                                    title={t("common.edit")}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    type="button"
                                                    onClick={() => deleteHandler(item)}
                                                    className="h-9 w-9 text-text-2 hover:text-status-danger hover:bg-status-danger/10"
                                                    title={t("common.delete")}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Render the dialog with the selected item */}
            <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-text-1">
                            {selectedItem ? t("common.edit") : t("common.add")} {tableTitle}
                        </DialogTitle>
                        <DialogDescription className="text-text-2">
                            {selectedItem 
                                ? t("common.editItemDesc", "Make your changes and click save.")
                                : t("common.addItemDesc", "Fill in the details and click save.")
                            }
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {columnProperties.map((property, index) => (
                            <div key={index} className="space-y-2">
                                <Label htmlFor={property} className="text-sm font-medium text-text-1">
                                    {columns[property]}
                                </Label>
                                <Input
                                    id={property}
                                    defaultValue={selectedItem?.[property] ?? ""}
                                    onChange={(e) => {
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        setSelectedItem((prevSelectedItem: any) => ({
                                            ...prevSelectedItem,
                                            [property]: e.target.value
                                        }));
                                    }}
                                    className="h-10"
                                />
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={handleCloseDialog}
                        >
                            {t("common.cancel")}
                        </Button>
                        {/* @ts-ignore */}
                        <Button 
                            type="submit" 
                            onClick={() => handleSave(selectedItem, selectedItem?.id)}
                            className="font-semibold"
                        >
                            {t("common.save")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export {TableFloatUI};
